import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Observable, catchError, finalize, forkJoin, of } from 'rxjs';

import { ComissaoEstatisticaAgrupadaResponse, ComissaoEstatisticaAnaliticoPageResponse, ComissaoEstatisticaReuniaoAnaliticoFiltroRequest } from '@/core/api/sicom-api/comissoes/estatisticas/comissao-estatistica.model';
import { ComissaoEstatisticaService } from '@/core/api/sicom-api/comissoes/estatisticas/comissao-estatistica.service';
import { ComissaoReuniaoFiltroRequest, EComissaoReuniaoTipoPeriodoFiltro } from '@/core/api/sicom-api/comissoes/reunioes/comissao-reuniao.model';
import { PageResponse, UsuarioResumoResponse } from '@/core/api/sicom-api/sistema/usuario/usuario.model';
import { UsuarioService } from '@/core/api/sicom-api/sistema/usuario/usuario.service';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { DatePicker } from 'primeng/datepicker';
import { type PaginatorState } from 'primeng/paginator';
import { SkeletonModule } from 'primeng/skeleton';

import { DetalhamentoPendencias } from '../../gestao-administrativa/detalhamento-pendencias/detalhamento-pendencias';
import { IndicadorPendencia } from '../../gestao-administrativa/mapa-pendencias/indicador-pendencia.model';
import { PendenciaLinha } from '../../gestao-administrativa/mapa-pendencias/pendencia-linha.model';
import { PainelComissoesContainer } from '../painel-comissoes/painel-comissoes';
import { RankingAssistente, excluirSemResponsavel, mapLinhaReuniaoAnalitico, montarRanking } from './ranking-reunioes.model';

type PapelRanking = 'assistente' | 'coordenador' | 'secretario';

interface ConfigPapel {
    titulo: string;
    subtitulo: string;
    legendaVazio: string;
    icone: string;
    colunaUf: string;
    carregar: (filtro: ComissaoReuniaoFiltroRequest) => Observable<ComissaoEstatisticaAgrupadaResponse[]>;
    consultarAnalitico: (filtro: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page: number, size: number) => Observable<ComissaoEstatisticaAnaliticoPageResponse>;
}

@Component({
    selector: 'app-ranking-reunioes',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, DatePicker, AvatarModule, SkeletonModule, DetalhamentoPendencias, PainelComissoesContainer],
    templateUrl: './ranking-reunioes.html',
    styleUrls: ['./ranking-reunioes.scss']
})
export class RankingReunioesComponent implements OnInit {
    private readonly estatisticaService = inject(ComissaoEstatisticaService);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly usuarioService = inject(UsuarioService);
    private readonly route = inject(ActivatedRoute);
    private readonly destroyRef = inject(DestroyRef);

    private readonly configs: Record<PapelRanking, ConfigPapel> = {
        assistente: {
            titulo: 'Produção dos assistentes',
            subtitulo: 'Ranking de assistentes por número de reuniões no período filtrado.',
            legendaVazio: 'Nenhum assistente com reuniões no período.',
            icone: 'pi pi-fw pi-star',
            colunaUf: 'ASSISTENTE',
            carregar: (filtro) => this.estatisticaService.listarReunioesPorAssistente(filtro),
            consultarAnalitico: (filtro, page, size) => this.estatisticaService.consultarAnaliticoReunioesPorAssistente(filtro, page, size)
        },
        coordenador: {
            titulo: 'Produção dos coordenadores',
            subtitulo: 'Ranking de coordenadores por número de reuniões no período filtrado.',
            legendaVazio: 'Nenhum coordenador com reuniões no período.',
            icone: 'pi pi-fw pi-id-card',
            colunaUf: 'COORDENADOR',
            carregar: (filtro) => this.estatisticaService.listarReunioesPorCoordenador(filtro),
            consultarAnalitico: (filtro, page, size) => this.estatisticaService.consultarAnaliticoReunioesPorCoordenador(filtro, page, size)
        },
        secretario: {
            titulo: 'Produção dos secretários',
            subtitulo: 'Ranking de secretários por número de reuniões no período filtrado.',
            legendaVazio: 'Nenhum secretário com reuniões no período.',
            icone: 'pi pi-fw pi-pencil',
            colunaUf: 'SECRETÁRIO',
            carregar: (filtro) => this.estatisticaService.listarReunioesPorSecretario(filtro),
            consultarAnalitico: (filtro, page, size) => this.estatisticaService.consultarAnaliticoReunioesPorSecretario(filtro, page, size)
        }
    };

    papel: PapelRanking = 'assistente';

    dataInicioDate: Date | null = new Date(new Date().getFullYear(), 0, 1);
    dataFimDate: Date | null = new Date(new Date().getFullYear(), 11, 31);

    carregando = false;
    ranking: RankingAssistente[] = [];

    modoAnalitico = false;
    pessoaSelecionada: RankingAssistente | null = null;
    linhasAnalitico: PendenciaLinha[] = [];
    firstAnalitico = 0;
    rowsAnalitico = 100;
    totalRegistrosAnalitico = 0;
    carregandoAnalitico = false;

    get config(): ConfigPapel {
        return this.configs[this.papel];
    }

    get podio(): RankingAssistente[] {
        return this.ranking.slice(0, 3);
    }

    get demais(): RankingAssistente[] {
        return this.ranking.slice(3);
    }

    get totalReunioes(): number {
        return this.ranking.reduce((total, item) => total + item.total, 0);
    }

    get totalPessoasRanking(): number {
        return this.ranking.length;
    }

    get maiorProducao(): number {
        return this.ranking[0]?.total ?? 0;
    }

    get rotuloPessoas(): string {
        if (this.papel === 'coordenador') {
            return 'Coordenadores';
        }
        if (this.papel === 'secretario') {
            return 'Secretários';
        }
        return 'Assistentes';
    }

    get periodoAnalisado(): string {
        return `${this.formatarDataExibicao(this.dataInicioDate)} a ${this.formatarDataExibicao(this.dataFimDate)}`;
    }

    ngOnInit(): void {
        const papelRota = this.route.snapshot.data['papel'];
        if (papelRota === 'coordenador' || papelRota === 'secretario') {
            this.papel = papelRota;
        } else {
            this.papel = 'assistente';
        }
        this.carregar();
    }

    carregar(): void {
        this.carregando = true;
        const filtro = this.criarFiltro();
        const usuariosVazio: PageResponse<UsuarioResumoResponse> = { content: [], totalElements: 0, totalPages: 0, size: 0, number: 0 };
        forkJoin({
            responsaveis: this.config.carregar(filtro).pipe(catchError(() => of<ComissaoEstatisticaAgrupadaResponse[]>([]))),
            usuarios: this.usuarioService.listar({ inAtivo: 'S', size: 500 }).pipe(catchError(() => of(usuariosVazio)))
        })
            .pipe(
                finalize(() => {
                    this.carregando = false;
                    // Mesma protecao aplicada ao ranking de conselheiros: sem isto a view pode ficar no skeleton.
                    this.cdr.markForCheck();
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(({ responsaveis, usuarios }) => {
                this.ranking = montarRanking(excluirSemResponsavel(responsaveis), usuarios.content);
            });
    }

    limpar(): void {
        this.dataInicioDate = new Date(new Date().getFullYear(), 0, 1);
        this.dataFimDate = new Date(new Date().getFullYear(), 11, 31);
        this.carregar();
    }

    abrirAnalitico(item: RankingAssistente): void {
        this.pessoaSelecionada = item;
        this.modoAnalitico = true;
        this.firstAnalitico = 0;
        this.linhasAnalitico = [];
        this.totalRegistrosAnalitico = 0;
        this.carregarAnalitico();
    }

    voltarRanking(): void {
        this.modoAnalitico = false;
        this.pessoaSelecionada = null;
        this.linhasAnalitico = [];
        this.totalRegistrosAnalitico = 0;
        this.firstAnalitico = 0;
        this.carregandoAnalitico = false;
        this.carregar();
    }

    onPageChangeAnalitico(event: PaginatorState): void {
        this.firstAnalitico = event.first ?? 0;
        this.rowsAnalitico = event.rows ?? this.rowsAnalitico;
        this.carregarAnalitico();
    }

    rotuloReuniao(total: number): string {
        return total === 1 ? 'reunião' : 'reuniões';
    }

    obterIndicadorAnalitico(): IndicadorPendencia | null {
        if (!this.pessoaSelecionada) {
            return null;
        }
        return {
            id: `ranking-${this.papel}`,
            titulo: this.pessoaSelecionada.nome,
            descricao: `Reuniões vinculadas a ${this.pessoaSelecionada.nome} no período filtrado.`,
            grupo: 'Operacional',
            classeGrupo: 'grupo-registro',
            quantidade: this.totalRegistrosAnalitico || null
        };
    }

    private carregarAnalitico(): void {
        if (!this.pessoaSelecionada) {
            return;
        }
        const rows = this.rowsAnalitico || 100;
        const page = Math.floor((this.firstAnalitico || 0) / rows);
        this.carregandoAnalitico = true;
        const filtro: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest = {
            ...this.criarFiltro(),
            codigoAgrupamento: this.pessoaSelecionada.codigo
        };
        this.config
            .consultarAnalitico(filtro, page, rows)
            .pipe(
                catchError(() => of({ content: [], totalElements: 0 } as ComissaoEstatisticaAnaliticoPageResponse)),
                finalize(() => {
                    this.carregandoAnalitico = false;
                    this.cdr.markForCheck();
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((resultado) => {
                this.linhasAnalitico = (resultado.content ?? []).map((item) => mapLinhaReuniaoAnalitico(item));
                this.totalRegistrosAnalitico = resultado.totalElements ?? this.linhasAnalitico.length;
            });
    }

    private criarFiltro(): ComissaoReuniaoFiltroRequest {
        return {
            tipoPeriodo: EComissaoReuniaoTipoPeriodoFiltro.POR_PERIODO,
            dataInicioReuniao: this.formatarData(this.dataInicioDate),
            dataFimReuniao: this.formatarData(this.dataFimDate),
            excluirStatusReuniaoExcluida: false
        };
    }

    private formatarData(data: Date | null): string | undefined {
        if (!data) {
            return undefined;
        }
        const ano = data.getFullYear();
        const mes = String(data.getMonth() + 1).padStart(2, '0');
        const dia = String(data.getDate()).padStart(2, '0');
        return `${ano}-${mes}-${dia}`;
    }

    private formatarDataExibicao(data: Date | null): string {
        if (!data) {
            return '-';
        }
        const dia = String(data.getDate()).padStart(2, '0');
        const mes = String(data.getMonth() + 1).padStart(2, '0');
        const ano = data.getFullYear();
        return `${dia}/${mes}/${ano}`;
    }
}
